import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Send, Check } from 'lucide-react';

// Same-origin call — nginx proxies /api to the local Node service.
const API_ENDPOINT = '/api/contact';

function Contact() {
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setStatus('sending');

    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: data.get('name'),
      email: data.get('email'),
      message: data.get('message'),
      website: data.get('website'), // honeypot
    };

    try {
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) {
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      setStatus('sent');
      form.reset();
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Something went wrong. Please email nate@sudcosolutions.com directly.');
    }
  }

  return (
    <section id="contact" className="relative py-24">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '0px 0px 300px 0px' }}
          transition={{ duration: 0.3 }}
          className="relative glass rounded-2xl p-8 md:p-12 overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-accent/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-accent-2/20 rounded-full blur-3xl pointer-events-none" />

          <div className="relative grid md:grid-cols-2 gap-10">
            <div>
              <p className="text-sm text-accent-2 font-medium mb-3 uppercase tracking-wider">Contact</p>
              <h2 className="font-display font-bold text-3xl md:text-4xl text-text mb-4">Let's build something.</h2>
              <p className="text-text-muted leading-relaxed mb-6">
                Tell me about your business or project. I'll respond within a day, usually faster.
              </p>
              <a
                href="mailto:nate@sudcosolutions.com"
                className="inline-flex items-center gap-2 text-text hover:gradient-text"
              >
                <Mail size={16} className="text-accent-2" />
                nate@sudcosolutions.com
              </a>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot — hidden from real users, bots will fill it */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              />

              <div>
                <label htmlFor="name" className="block text-xs text-text-muted mb-1.5">Your name</label>
                <input
                  id="name"
                  name="name"
                  required
                  className="w-full bg-surface-2/60 border border-border rounded-xl px-4 py-3 text-text placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs text-text-muted mb-1.5">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full bg-surface-2/60 border border-border rounded-xl px-4 py-3 text-text placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                  placeholder="you@business.com"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-xs text-text-muted mb-1.5">What do you need?</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  className="w-full bg-surface-2/60 border border-border rounded-xl px-4 py-3 text-text placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors resize-none"
                  placeholder="A new site for my bakery, AI features for my app, etc."
                />
              </div>
              <button
                type="submit"
                disabled={status === 'sending' || status === 'sent'}
                className="w-full bg-gradient-brand text-white px-6 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2 hover:shadow-glow transition-shadow disabled:opacity-60"
              >
                {status === 'sending' && 'Sending...'}
                {status === 'sent' && (<><Check size={16} /> Sent — talk soon</>)}
                {(status === 'idle' || status === 'error') && (<><Send size={16} /> Send message</>)}
              </button>
              {error && <p className="text-sm text-red-400">{error}</p>}
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Contact;
